// apps/admin-ui/src/components/governance/CreateProposalModal.tsx
// ALL RIGHTS RESERVED

/**
 * Create Proposal Modal Component
 *
 * Form for creating new governance proposals.
 * Migrated from apps/csr — tokens adapted to admin-ui design system.
 *
 * @module apps/admin-ui/components/governance/CreateProposalModal
 */

import { useState } from 'react';
import { Modal } from '../Modal';
import { createProposal, submitProposal } from '../../api/governance';

// ═══════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════

interface CreateProposalModalProps {
  onClose: () => void;
  onCreated: () => void;
}

type ProposalTypeOption = {
  value: string;
  label: string;
  description: string;
};

// ═══════════════════════════════════════════════════════
// CONSTANTS
// ═══════════════════════════════════════════════════════

const PROPOSAL_TYPES: ProposalTypeOption[] = [
  { 
    value: 'feature_request', 
    label: 'Feature Request',
    description: 'Propose a new feature or enhancement for the platform',
  },
  { 
    value: 'policy_update', 
    label: 'Policy Update',
    description: 'Update operational policies or procedures',
  },
  { 
    value: 'fee_adjustment', 
    label: 'Fee Adjustment',
    description: 'Change fee rates for minting, redeeming, or transfers',
  },
  { 
    value: 'member_add', 
    label: 'Add Council Member',
    description: 'Add a new council, board, or advisor member',
  },
  { 
    value: 'member_remove', 
    label: 'Remove Council Member',
    description: 'Remove an existing member from governance',
  },
  { 
    value: 'parameter_change', 
    label: 'System Parameter Change',
    description: 'Modify system parameters or configuration values',
  },
  { 
    value: 'reserve_action', 
    label: 'Reserve Action',
    description: 'Actions affecting reserve management or composition',
  },
  { 
    value: 'compliance_update', 
    label: 'Compliance Update',
    description: 'Changes to compliance rules or requirements',
  },
];

const MIN_TITLE_LENGTH = 10;
const MIN_DESCRIPTION_LENGTH = 50;

// ═══════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════

export function CreateProposalModal({ onClose, onCreated }: CreateProposalModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [proposalType, setProposalType] = useState(PROPOSAL_TYPES[0].value);
  const [submitImmediately, setSubmitImmediately] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Validation
  const titleValid = title.length >= MIN_TITLE_LENGTH;
  const descriptionValid = description.length >= MIN_DESCRIPTION_LENGTH;
  const isValid = titleValid && descriptionValid;

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!isValid) return;

    try {
      setLoading(true);
      setError(null);

      // Create the proposal
      const { proposal } = await createProposal({
        title,
        description,
        proposalType,
      });

      // Optionally submit for voting immediately
      if (submitImmediately) {
        await submitProposal(proposal.id);
      }

      onCreated();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create proposal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={true} onClose={onClose} title="New Proposal">
      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-content mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter proposal title..."
            className={`w-full px-3 py-2 border rounded-md bg-surface text-sm ${
              title.length > 0 && !titleValid 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-edge focus:ring-brand-500'
            }`}
            disabled={loading}
          />
          <p className={`text-xs mt-1 ${title.length > 0 && !titleValid ? 'text-red-500' : 'text-content-muted'}`}>
            {title.length}/{MIN_TITLE_LENGTH} characters minimum
          </p>
        </div>

        {/* Type */}
        <div>
          <label className="block text-sm font-medium text-content mb-1">
            Type
          </label>
          <select
            value={proposalType}
            onChange={(e) => setProposalType(e.target.value)}
            className="w-full px-3 py-2 border border-edge rounded-md bg-surface text-sm"
            disabled={loading}
          >
            {PROPOSAL_TYPES.map(type => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
          <p className="text-xs text-content-muted mt-1">
            {PROPOSAL_TYPES.find(t => t.value === proposalType)?.description}
          </p>
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-content mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Describe the proposal in detail. Include any JSON payload if needed..."
            rows={6}
            className={`w-full px-3 py-2 border rounded-md bg-surface text-sm resize-none ${
              description.length > 0 && !descriptionValid 
                ? 'border-red-300 focus:ring-red-500' 
                : 'border-edge focus:ring-brand-500'
            }`}
            disabled={loading}
          />
          <p className={`text-xs mt-1 ${description.length > 0 && !descriptionValid ? 'text-red-500' : 'text-content-muted'}`}>
            {description.length}/{MIN_DESCRIPTION_LENGTH} characters minimum
          </p>
        </div>

        {/* Submit Immediately Checkbox */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="submitImmediately"
            checked={submitImmediately}
            onChange={(e) => setSubmitImmediately(e.target.checked)}
            className="rounded border-edge"
            disabled={loading}
          />
          <label htmlFor="submitImmediately" className="text-sm text-content-secondary">
            Submit for voting immediately
          </label>
        </div>

        {/* Help Text */}
        <div className="bg-surface-emphasis p-3 rounded-md text-xs text-content-secondary">
          <p className="font-medium text-content mb-1">Tips:</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Include any payload details in the description (JSON format if needed)</li>
            <li>Draft proposals can be edited before submission</li>
            <li>Voting period is 7 days once submitted</li>
            <li>5 yes votes required to pass; 3 vetoes to block</li>
          </ul>
        </div>

        {/* Error */}
        {error && (
          <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
            {error}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-content-secondary hover:text-content transition-colors"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!isValid || loading}
            className="px-4 py-2 bg-brand-500 text-white rounded-md hover:bg-brand-500/90 transition-colors disabled:opacity-50 text-sm"
          >
            {loading 
              ? 'Creating...' 
              : submitImmediately 
                ? 'Create & Submit' 
                : 'Create Draft'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
